import { useState, useEffect, useCallback, useRef } from 'react';
import { audioLibraryService } from '../services/audioLibraryService';
import { authService } from '../services/auth';

export interface AudioLibraryItem {
  id: string;
  user_id: string;
  mod_approved: boolean;
  is_private: boolean;
  audio_type: 'music' | 'background' | 'sound';
  name: string;
  description: string | null;
  audio_file_path: string;
  waveform_file_path: string | null;
  audio_duration_seconds: number | null;
  ai_provider: string | null;
  use_count: number;
  thumbs_up: number;
  thumbs_down: number;
  created_at: string;
  updated_at: string;
  user_rating?: 'like' | 'dislike' | null;
}

export interface AudioLibraryFilters {
  audio_type?: 'music' | 'background' | 'sound';
  search?: string;
  sort?: 'popular' | 'recent' | 'rating' | 'alphabetical' | 'my_favorites';
  page?: number;
  limit?: number;
  duration_range?: 'under_1' | '1_to_2' | '2_to_5' | '5_to_10' | 'over_10';
  my_uploads_only?: boolean;
}

export interface AudioLibraryState {
  items: AudioLibraryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  filters: AudioLibraryFilters;
  userRatings: Record<string, 'like' | 'dislike'>;
}

export interface AudioLibraryControls {
  loadLibrary: (filters?: AudioLibraryFilters) => Promise<void>;
  loadMore: () => Promise<void>;
  rateItem: (itemId: string, rating: 'like' | 'dislike') => Promise<void>;
  uploadAudio: (file: File, metadata: {
    name: string;
    description?: string;
    audio_type: 'music' | 'background' | 'sound';
    is_private?: boolean;
  }) => Promise<AudioLibraryItem>;
  deleteItem: (itemId: string) => Promise<void>;
  setFilters: (filters: AudioLibraryFilters) => void;
  getAudioUrl: (item: AudioLibraryItem) => string;
  previewAudio: (item: AudioLibraryItem) => Promise<HTMLAudioElement>;
  clearError: () => void;
}

export const useAudioLibrary = (): AudioLibraryState & AudioLibraryControls => {
  const [items, setItems] = useState<AudioLibraryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<AudioLibraryFilters>({});
  const [userRatings, setUserRatings] = useState<Record<string, 'like' | 'dislike'>>({});
  const filtersRef = useRef<AudioLibraryFilters>({});

  // Update filters ref when filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Load library items - stable function using ref to avoid circular dependency
  const loadLibrary = useCallback(async (filterOverrides?: AudioLibraryFilters) => {
    setIsLoading(true);
    setError(null);

    try {
      const finalFilters = { ...filtersRef.current, ...filterOverrides };
      if (finalFilters.audio_type == null) {
        setIsLoading(false);
        console.log('finalFilters is null');
        return;
      }
      const result = await audioLibraryService.getAudioLibraryItems(finalFilters);
      
      setItems(result.items);
      setTotal(result.total);
      setPage(result.page);
      setLimit(result.limit);
      setTotalPages(result.totalPages);
      
      // Update user ratings
      const ratings: Record<string, 'like' | 'dislike'> = {};
      result.items.forEach(item => {
        if (item.user_rating) {
          ratings[item.id] = item.user_rating;
        }
      });
      setUserRatings(ratings);

      console.log(`Loaded ${result.items.length} audio library items`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load audio library';
      setError(message);
      console.error('Load library error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load more items (pagination)
  const loadMore = useCallback(async () => {
    if (isLoading || page >= totalPages) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await audioLibraryService.getAudioLibraryItems({
        ...filters,
        page: page + 1,
        limit
      });
      
      setItems(prev => [...prev, ...result.items]);
      setPage(result.page);
      
      // Update user ratings
      const ratings: Record<string, 'like' | 'dislike'> = { ...userRatings };
      result.items.forEach(item => {
        if (item.user_rating) {
          ratings[item.id] = item.user_rating;
        }
      });
      setUserRatings(ratings);

      console.log(`Loaded ${result.items.length} more audio library items`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load more items';
      setError(message);
      console.error('Load more error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, page, totalPages, filters, limit, userRatings]);

  // Rate an item (or remove rating if clicking current vote)
  const rateItem = useCallback(async (itemId: string, rating: 'like' | 'dislike') => {
    try {
      // Check if user is clicking their current vote to remove it
      const currentRating = userRatings[itemId];
      const shouldRemoveVote = currentRating === rating;
      
      if (shouldRemoveVote) {
        // Remove the vote by calling the API with null or handle removal
        await audioLibraryService.removeRating(itemId);
        
        // Update local state - remove rating
        setUserRatings(prev => {
          const { [itemId]: _, ...rest } = prev;
          return rest;
        });
        
        // Update item counts in the list
        setItems(prev => prev.map(item => {
          if (item.id !== itemId) return item;
          
          let newThumbsUp = item.thumbs_up;
          let newThumbsDown = item.thumbs_down;
          
          if (currentRating === 'like') {
            newThumbsUp--;
          } else if (currentRating === 'dislike') {
            newThumbsDown--;
          }
          
          return {
            ...item,
            thumbs_up: newThumbsUp,
            thumbs_down: newThumbsDown,
            user_rating: null
          };
        }));
        
        console.log(`Removed rating for item ${itemId}`);
      } else {
        // Set new rating
        await audioLibraryService.rateAudioLibraryItem(itemId, rating);
        
        // Update local state
        setUserRatings(prev => ({ ...prev, [itemId]: rating }));
        
        // Update item counts in the list
        setItems(prev => prev.map(item => {
          if (item.id !== itemId) return item;
          
          const wasLiked = item.user_rating === 'like';
          const wasDisliked = item.user_rating === 'dislike';
          const isLiking = rating === 'like';
          
          let newThumbsUp = item.thumbs_up;
          let newThumbsDown = item.thumbs_down;
          
          if (wasLiked && !isLiking) {
            newThumbsUp--;
          } else if (!wasLiked && isLiking) {
            newThumbsUp++;
          }
          
          if (wasDisliked && isLiking) {
            newThumbsDown--;
          } else if (!wasDisliked && !isLiking) {
            newThumbsDown++;
          }
          
          return {
            ...item,
            thumbs_up: newThumbsUp,
            thumbs_down: newThumbsDown,
            user_rating: rating
          };
        }));
        
        console.log(`Rated item ${itemId} as ${rating}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to rate item';
      setError(message);
      console.error('Rate item error:', err);
    }
  }, [userRatings]);

  // Upload new audio
  const uploadAudio = useCallback(async (file: File, metadata: {
    name: string;
    description?: string;
    audio_type: 'music' | 'background' | 'sound';
    is_private?: boolean;
  }): Promise<AudioLibraryItem> => {
    setIsLoading(true);
    setError(null);

    try {
      const newItem = await audioLibraryService.uploadAudioLibraryItem(file, metadata);
      
      // Add to local state if it matches current filters
      if (!filters.audio_type || filters.audio_type === newItem.audio_type) {
        setItems(prev => [newItem, ...prev]);
        setTotal(prev => prev + 1);
      }
      
      console.log(`Uploaded audio: ${newItem.name}`);
      return newItem;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload audio';
      setError(message);
      console.error('Upload audio error:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [filters.audio_type]);

  // Delete an item
  const deleteItem = useCallback(async (itemId: string) => {
    try {
      await audioLibraryService.deleteAudioLibraryItem(itemId);
      
      // Remove from local state
      setItems(prev => prev.filter(item => item.id !== itemId));
      setTotal(prev => prev - 1);
      
      // Remove from ratings
      setUserRatings(prev => {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      });
      
      console.log(`Deleted item ${itemId}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete item';
      setError(message);
      console.error('Delete item error:', err);
    }
  }, []);

  // Set filters and reload
  const setFilters = useCallback((newFilters: AudioLibraryFilters) => {
    setFiltersState(newFilters);
    // Only reset page if not explicitly set in newFilters
    if (newFilters.page !== undefined) {
      setPage(newFilters.page);
    } else {
      setPage(1);
    }
    setItems([]);
    if (authService.isAuthenticated()) {
      // Call loadLibrary directly with the new filters
      loadLibrary(newFilters);
    }
  }, [loadLibrary]);

  // Get audio URL for playing
  const getAudioUrl = useCallback((item: AudioLibraryItem): string => {
    return audioLibraryService.getAudioUrl(item);
  }, []);

  // Preview audio
  const previewAudio = useCallback(async (item: AudioLibraryItem): Promise<HTMLAudioElement> => {
    const audioUrl = getAudioUrl(item);
    const audio = new Audio(audioUrl);
    
    return new Promise((resolve, reject) => {
      audio.addEventListener('canplaythrough', () => resolve(audio), { once: true });
      audio.addEventListener('error', reject, { once: true });
      audio.preload = 'auto';
    });
  }, [getAudioUrl]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load initial data only once
  useEffect(() => {
    if (authService.isAuthenticated()) {
      loadLibrary();
    }
  }, [loadLibrary]);

  return {
    // State
    items,
    total,
    page,
    limit,
    totalPages,
    isLoading,
    error,
    filters,
    userRatings,

    // Controls
    loadLibrary,
    loadMore,
    rateItem,
    uploadAudio,
    deleteItem,
    setFilters,
    getAudioUrl,
    previewAudio,
    clearError
  };
};